import { useState } from "react";
import { Folder, FolderX, FolderPlus, X, Loader2 } from "lucide-react";

/**
 * Modal that lets the admin move one or many videos into a folder.
 *
 * Props:
 *   open              – bool
 *   folders           – [{id, name, color, video_count}]
 *   videoCount        – how many videos will be moved (for the header text)
 *   currentFolderId   – null | string. When set, "Aus Ordner entfernen"
 *                       is shown as an explicit option.
 *   onClose()         – close without change
 *   onMove(folderId)  – Promise. folderId == null → remove from folder.
 *   onCreateFolder(name, color) -> Promise<{id,...}>  (optional; renders
 *                       the "Neuer Ordner…" affordance when supplied)
 */
export default function MoveToFolderModal({
  open,
  folders,
  videoCount = 1,
  currentFolderId = null,
  onClose,
  onMove,
  onCreateFolder,
}) {
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  if (!open) return null;

  const doMove = async (folderId) => {
    if (busy) return;
    setBusy(true);
    try {
      await onMove(folderId);
    } finally {
      setBusy(false);
    }
  };

  const doCreateAndMove = async (e) => {
    e?.preventDefault?.();
    const n = newName.trim();
    if (!n || busy) return;
    setBusy(true);
    try {
      const f = await onCreateFolder(n, "#4a90e2");
      await onMove(f.id);
      setNewName("");
      setCreating(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="move-to-folder-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-[#2a2a30] bg-[#0d0d0e] shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#2a2a30]">
          <h3 className="text-sm font-semibold text-white">
            {videoCount === 1
              ? "In Ordner verschieben"
              : `${videoCount} Videos in Ordner verschieben`}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-md inline-flex items-center justify-center text-[#9a9aa3] hover:text-white hover:bg-[#17171a]"
            aria-label="Schließen"
            data-testid="move-to-folder-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2 space-y-1">
          {currentFolderId && (
            <button
              type="button"
              onClick={() => doMove(null)}
              disabled={busy}
              className="w-full flex items-center gap-2 px-3 h-10 rounded-md text-sm text-amber-300 hover:bg-amber-500/10 disabled:opacity-50"
              data-testid="move-to-folder-clear"
            >
              <FolderX size={14} />
              <span className="flex-1 text-left">Aus Ordner entfernen</span>
            </button>
          )}

          {folders.length === 0 && !creating && (
            <p className="text-xs text-[#9a9aa3] px-3 py-4 text-center">
              Noch keine Ordner. Erstelle deinen ersten Ordner mit dem Button unten.
            </p>
          )}

          {folders.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => doMove(f.id)}
              disabled={busy || f.id === currentFolderId}
              className={`w-full flex items-center gap-2 px-3 h-10 rounded-md text-sm transition-colors ${
                f.id === currentFolderId
                  ? "text-[#5a5a60] cursor-not-allowed"
                  : "text-[#c8c8d0] hover:bg-[#17171a]"
              } disabled:opacity-60`}
              data-testid={`move-to-folder-option-${f.id}`}
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: f.color }}
              />
              <Folder size={14} className="text-[#9a9aa3]" />
              <span className="flex-1 text-left truncate">{f.name}</span>
              <span className="text-[10px] text-[#5a5a60] tabular-nums">
                {f.video_count}
              </span>
              {f.id === currentFolderId && (
                <span className="text-[10px] text-[#5a5a60]">aktuell</span>
              )}
            </button>
          ))}
        </div>

        <div className="border-t border-[#2a2a30] p-2">
          {creating ? (
            <form
              onSubmit={doCreateAndMove}
              className="flex items-center gap-2 p-1"
              data-testid="move-to-folder-create-form"
            >
              <FolderPlus size={14} className="text-[#9a9aa3] shrink-0" />
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ordnername"
                maxLength={60}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setCreating(false);
                    setNewName("");
                  }
                }}
                className="flex-1 h-9 px-2 rounded bg-[#0a0a0b] text-sm text-white outline-none border border-[#2a2a30] focus:border-[#ff6b35]"
                data-testid="move-to-folder-create-input"
              />
              <button
                type="submit"
                disabled={!newName.trim() || busy}
                className="h-9 px-3 rounded bg-[#ff6b35] text-white text-xs font-medium disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="move-to-folder-create-submit"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                Erstellen & Verschieben
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setNewName("");
                }}
                className="text-xs text-[#9a9aa3] hover:text-white px-2"
              >
                Abbrechen
              </button>
            </form>
          ) : (
            onCreateFolder && (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="w-full flex items-center gap-2 px-3 h-10 rounded-md text-sm text-[#9a9aa3] hover:text-white hover:bg-[#17171a]"
                data-testid="move-to-folder-new-btn"
              >
                <FolderPlus size={14} /> Neuer Ordner…
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
